/* eslint-disable @typescript-eslint/no-explicit-any */
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { dbGetAll, dbPut, dbDelete } from './db';

// Fila de lançamentos feitos sem internet, enviados pro servidor na ordem
// em que foram criados assim que a conexão volta (ver sincronizacao.ts).
// A ordem importa: a abertura de caixa precisa chegar antes das vendas
// daquele caixa (o backend vincula a venda à sessão aberta do usuário), e
// um parceiro novo antes da venda que usa ele.

// 'venda' = venda do PDV (POST /vendaspdv); 'vendafront' = venda da tela de
// vendas do FrontSIAC (POST /vendas). Os dois apps sincronizam os dois tipos
// (mesmo banco local, ver db.ts).
export type TTipoFila = 'abrirsessao' | 'parceiro' | 'venda' | 'vendafront';
export type TStatusFila = 'pendente' | 'erro';

export interface IItemFila {
  id: string;
  tipo: TTipoFila;
  criadoem: string;
  // Usuário logado quando o lançamento foi feito — só ele sincroniza esse
  // item (a abertura de caixa e o vínculo da venda com a sessão dependem do
  // usuário do token que envia).
  iduser: string;
  // Caixa (header idconta) em que o lançamento foi feito.
  idconta: string;
  // Corpo exato que vai pro servidor.
  payload: any;
  // Dados extras só pro PDV (impressão do cupom offline, resumo na lista
  // de pendentes) — nunca enviados.
  extra?: any;
  status: TStatusFila;
  erro?: string;
  tentativas: number;
}

// `est` = a empresa controla estoque (mesmo campo do token usado nas telas).
export const usuarioLogado = (): { iduser: string; nome: string; idempresa: string; est: boolean } => {
  try {
    const token = localStorage.getItem('APP_ACCESS_TOKEN');
    if (!token) return { iduser: '', nome: '', idempresa: '', est: false };
    let valor = token;
    try { valor = JSON.parse(token); } catch { /* token salvo sem aspas */ }
    const payload = jwtDecode<{ iduser?: string; nome?: string; login?: string; idempresa?: string; est?: boolean }>(valor);
    return { iduser: payload.iduser || '', nome: payload.nome || payload.login || '', idempresa: payload.idempresa || '', est: !!payload.est };
  } catch {
    return { iduser: '', nome: '', idempresa: '', est: false };
  }
};

type TOuvinte = () => void;
const ouvintes = new Set<TOuvinte>();
export const notificarFila = () => ouvintes.forEach((ouvinte) => ouvinte());
export const inscreverFila = (ouvinte: TOuvinte) => {
  ouvintes.add(ouvinte);
  return () => { ouvintes.delete(ouvinte); };
};

export const listarFila = async (): Promise<IItemFila[]> => {
  try {
    const itens = await dbGetAll<IItemFila>('fila');
    return itens.sort((a, b) => a.criadoem.localeCompare(b.criadoem));
  } catch {
    return [];
  }
};

export const enfileirar = async (
  tipo: TTipoFila, idconta: string, payload: any, extra?: any, id: string = uuidv4(),
): Promise<IItemFila> => {
  const item: IItemFila = {
    id,
    tipo,
    // ISO com milissegundos — ordena certo mesmo com lançamentos no mesmo segundo.
    criadoem: new Date().toISOString(),
    iduser: usuarioLogado().iduser,
    idconta,
    payload,
    extra,
    status: 'pendente',
    tentativas: 0,
  };
  await dbPut('fila', item);
  notificarFila();
  return item;
};

export const atualizarItemFila = async (item: IItemFila) => {
  await dbPut('fila', item);
  notificarFila();
};

export const removerDaFila = async (id: string) => {
  await dbDelete('fila', id);
  notificarFila();
};

// Venda ainda não enviada (a chave do item de venda na fila é o próprio idmov).
export const buscarVendaNaFila = async (idmov: string): Promise<IItemFila | undefined> => {
  const itens = await listarFila();
  return itens.find((item) => (item.tipo === 'venda' || item.tipo === 'vendafront') && item.id === idmov);
};
